import {
  ArrowUturnLeftIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import type { ReactElement } from 'react'
import {
  getDailyKey,
  getHabitDay,
  isReadOnlyFutureDate,
  isSameCalendarDay,
  parseDailyKey,
} from '../utils/dateHelpers'

interface DateNavProps {
  selectedDate: Date
  realToday: Date
  onChange: (date: Date) => void
  dayStartTime?: string
  isDesktop?: boolean
}

export function DateNav({
  selectedDate,
  realToday,
  onChange,
  dayStartTime = '00:00',
  isDesktop = false,
}: DateNavProps): ReactElement {
  const baseDate = getHabitDay(selectedDate, dayStartTime)
  const habitToday = getHabitDay(realToday, dayStartTime)
  const count = isDesktop ? 7 : 5
  const offset = isDesktop ? 3 : 2
  const days: Date[] = Array.from({ length: count }, (_, i) => {
    const d = new Date(baseDate)
    d.setDate(baseDate.getDate() - offset + i)
    return d
  })

  return (
    <div className="mb-4 flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onChange(getHabitDay(realToday, dayStartTime))}
        aria-label="Go to today"
        className="flex shrink-0 items-center gap-1 rounded-xl bg-indigo-100 px-2.5 py-2 font-medium text-indigo-700 text-xs hover:bg-indigo-200"
      >
        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
        Today
      </button>
      <div className="flex justify-center gap-1 overflow-x-auto pb-1">
        {days.map((day) => {
          const isSelected = isSameCalendarDay(day, baseDate, dayStartTime)
          const isToday = isSameCalendarDay(day, habitToday, dayStartTime)
          const isReadOnly = isReadOnlyFutureDate(day, habitToday, dayStartTime)
          return (
            <button
              key={getDailyKey(day, dayStartTime)}
              type="button"
              onClick={() => onChange(getHabitDay(day, dayStartTime))}
              className={`flex shrink-0 flex-col items-center rounded-xl px-2.5 py-2 font-medium text-xs transition-colors ${
                isSelected
                  ? 'bg-indigo-600 text-white shadow'
                  : isReadOnly
                    ? 'bg-gray-100 text-gray-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="text-[10px] uppercase tracking-wide">
                {isToday && !isSelected
                  ? 'Today'
                  : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][
                      day.getDay()
                    ]}
              </span>
              <span className="mt-0.5 font-semibold text-sm">
                {day.getDate()}
              </span>
              {isToday && isSelected && (
                <span className="mt-0.5 h-1 w-1 rounded-full bg-white/80" />
              )}
            </button>
          )
        })}
      </div>
      <label
        aria-label="Pick a date"
        className="relative shrink-0 cursor-pointer rounded-xl bg-gray-100 p-2.5 text-gray-500 hover:bg-gray-200"
      >
        <CalendarDaysIcon className="h-5 w-5" />
        <input
          type="date"
          className="absolute inset-0 cursor-pointer opacity-0"
          value={getDailyKey(baseDate, dayStartTime)}
          onChange={(e) => {
            if (!e.target.value) return
            const next = parseDailyKey(e.target.value, dayStartTime)
            onChange(next)
          }}
          aria-label="Select date"
        />
      </label>
    </div>
  )
}
